import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import mapEventDetailFromApi from 'rtkQuery/sharespro/mapper/modules/event/mapEventDetailFromApi';
import {
  EventCategoryFullListItem,
  EventCategoryShortListItem,
  EventCategoryDetail,
} from 'model/modules/events/eventCategory';
import { EventDetail } from 'model/modules/events/eventDetail';
import { EventListItem } from 'model/modules/events/eventList';
import { mapEventPaginationListFromApi } from 'rtkQuery/sharespro/mapper/modules/event/mapEventListFromApi';
import mapEventSchedulesFromApi from 'rtkQuery/sharespro/mapper/modules/event/mapEventSchedulesFromApi';
import mapEventCategoryFromApi, {
  mapFullEventCategories,
  mapShortEventCategories,
} from 'rtkQuery/sharespro/mapper/modules/event/mapEventCategoryFromApi';

export const eventsApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchEvents: builder.query<
      SharesproApiResponse<ApiPaginationListResponse<ApiEventListItem>>,
      void
    >({
      query: () => ({
        url: '/v3/community/event/list',
        sendToken: true,
        params: {
          // Hardcoded
          page: 1,
        },
      }),
    }),
    fetchEventDetail: builder.query<EventDetail, ID>({
      query: (id) => ({
        url: '/v3/community/event/detail',
        sendToken: true,
        params: {
          id,
        },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiEventDetail>
      ) {
        return mapEventDetailFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchShortEventTypes: builder.query<
      RemoteList<EventCategoryShortListItem>,
      void
    >({
      query: () => ({
        url: '/v3/community/event-type/list',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiListResponse<ApiEventTypeFullListItem>
        >
      ) {
        return mapShortEventCategories(baseQueryReturnValue.data);
      },
    }),
    fetchFullEventTypes: builder.query<
      RemoteList<EventCategoryFullListItem>,
      void
    >({
      query: () => ({
        url: '/v3/community/event/types',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiListResponse<ApiEventTypeFullListItem>
        >
      ) {
        return mapFullEventCategories(baseQueryReturnValue.data);
      },
    }),
    fetchUpcomingEvents: builder.query<PaginationList<EventListItem>, void>({
      query: () => ({
        url: '/v3/community/event/future',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiEventListItem>
        >
      ) {
        return mapEventPaginationListFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPassedEvents: builder.query<PaginationList<EventListItem>, void>({
      query: () => ({
        url: '/v3/community/event/past',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiEventListItem>
        >
      ) {
        return mapEventPaginationListFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchEventsSchedule: builder.query<RemoteList<ScheduledEventItem>, void>({
      query: () => ({
        url: '/v3/community/event/schedule',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiListResponse<ApiEventScheduleItem>
        >
      ) {
        return mapEventSchedulesFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchEventCategoryDetail: builder.query<EventCategoryDetail, ID>({
      query: (id) => ({
        url: '/v3/community/event-type/detail',
        sendToken: true,
        params: {
          id,
        },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiEventType>
      ) {
        return mapEventCategoryFromApi(baseQueryReturnValue.data);
      },
    }),
    joinEvent: builder.mutation<void, ID>({
      query: (id) => {
        return {
          url: '/v3/community/event/join',
          method: 'POST',
          sendToken: true,
          params: {
            id,
          },
        };
      },
    }),
    requestQueueForEvent: builder.mutation<void, ID>({
      query: (id) => {
        return {
          url: '/v3/community/event-type/wait',
          method: 'POST',
          sendToken: true,
          params: {
            id,
          },
        };
      },
    }),
  }),
});

export const {
  useFetchEventDetailQuery,
  useFetchEventsQuery,
  useFetchShortEventTypesQuery,
  useFetchFullEventTypesQuery,
  useFetchUpcomingEventsQuery,
  useFetchPassedEventsQuery,
  useFetchEventsScheduleQuery,
  useFetchEventCategoryDetailQuery,
  useJoinEventMutation,
  useRequestQueueForEventMutation,
} = eventsApi;
